import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { History, Search, Filter, Download, Loader2, ChevronDown, ChevronUp, Gift, Tag } from "lucide-react";
import { toast } from "sonner";

const DATE_PRESETS = [
  { key: "thismonth", label: "This month" },
  { key: "7", label: "7 days" },
  { key: "30", label: "30 days" },
  { key: "90", label: "90 days" },
  { key: "custom", label: "Custom" },
  { key: "all", label: "All time" },
];
const KIND_OPTIONS = [
  { v: "all", label: "All types" },
  { v: "promo", label: "Promotions" },
  { v: "loyalty", label: "Loyalty" },
];

function toIsoDate(daysAgo) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function firstOfMonthIso() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function humanDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

/**
 * RedemptionsLogCard
 *
 * Business-side audit trail. Merges promo redemptions (`promo_events` type
 * "redeemed") and loyalty redemptions (`loyalty_redemptions`) into one table
 * with masked names, filterable by type / promotion / date range / customer
 * substring. CSV export lives at the same endpoint with `?format=csv`.
 */
export default function RedemptionsLogCard() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ count: 0, promo: 0, loyalty: 0 });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [type, setType] = useState("all");
  const [promoId, setPromoId] = useState("all");
  const [q, setQ] = useState("");
  const [preset, setPreset] = useState("30");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [promos, setPromos] = useState([]);
  const cardRef = useRef(null);

  // Listen for a "jump here" event dispatched from other cards (e.g., Marketing Report KPI tile).
  useEffect(() => {
    const onOpen = (ev) => {
      const nextPreset = ev?.detail?.preset;
      if (nextPreset) setPreset(nextPreset);
      setType("all");
      setPromoId("all");
      setQ("");
      setOpen(true);
      // Scroll into view after the accordion has expanded on the next frame.
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };
    window.addEventListener("open-redemptions-log", onOpen);
    return () => window.removeEventListener("open-redemptions-log", onOpen);
  }, []);

  const effectiveDates = useMemo(() => {
    if (preset === "all") return { from: "", to: "" };
    if (preset === "thismonth") return { from: firstOfMonthIso(), to: "" };
    if (preset === "custom") return { from: dateFrom || "", to: dateTo || "" };
    return { from: toIsoDate(parseInt(preset, 10)), to: "" };
  }, [preset, dateFrom, dateTo]);

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (type !== "all") p.type = type;
    if (promoId !== "all") p.promo_id = promoId;
    if (q.trim()) p.q = q.trim();
    if (effectiveDates.from) p.date_from = effectiveDates.from;
    if (effectiveDates.to) p.date_to = effectiveDates.to;
    return { ...p, ...extra };
  }, [type, promoId, q, effectiveDates]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/business/redemptions", { params: buildParams() });
      setItems(data.items || []);
      setTotals(data.totals || { count: 0, promo: 0, loyalty: 0 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (!open) return;
    // load promo list once when panel opens
    api.get("/me/promotions")
      .then(({ data }) => setPromos(Array.isArray(data) ? data : (data.items || [])))
      .catch(() => setPromos([]));
    load();
  }, [open, load]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/me/business/redemptions", {
        params: buildParams({ format: "csv" }),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `redemptions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card ref={cardRef} className="rounded-md border border-border p-6" data-testid="redemptions-log-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <History size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-xl">Redemptions log</h3>
            {open && (
              <>
                <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-[#B31E1E]/40 text-[#B31E1E]" data-testid="redemptions-total-badge">
                  {totals.count} entries
                </Badge>
                {totals.promo > 0 && (
                  <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
                    <Tag size={10} className="mr-1" /> {totals.promo} promo
                  </Badge>
                )}
                {totals.loyalty > 0 && (
                  <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
                    <Gift size={10} className="mr-1" /> {totals.loyalty} loyalty
                  </Badge>
                )}
              </>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Every promotion coupon and loyalty reward you&apos;ve marked as
            redeemed, filterable by date, type, promotion, or customer name.
            Names are masked (initial + last name) — safe to share with your
            bookkeeper via CSV.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setOpen((o) => !o)}
          variant={open ? "outline" : "default"}
          className={`rounded-[35px] shadow-sm hover:shadow-md ${!open ? "bg-[#B31E1E] hover:bg-[#8F1717]" : ""}`}
          data-testid="redemptions-toggle-button"
        >
          {open ? <><ChevronUp size={14} className="mr-1.5" /> Hide log</> : <><ChevronDown size={14} className="mr-1.5" /> View redemptions log</>}
        </Button>
      </div>

      {open && (
        <>
          <div className="mt-5 border border-border rounded-md p-4 bg-gray-50/40" data-testid="redemptions-filters">
            <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider text-gray-500">
              <Filter size={12} /> Filters
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="rounded-md mt-1" data-testid="redemptions-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Promotion</Label>
                <Select value={promoId} onValueChange={setPromoId} disabled={type === "loyalty"}>
                  <SelectTrigger className="rounded-md mt-1" data-testid="redemptions-filter-promo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All promotions</SelectItem>
                    {promos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title || "(untitled)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Date range</Label>
                <Select value={preset} onValueChange={setPreset}>
                  <SelectTrigger className="rounded-md mt-1" data-testid="redemptions-filter-preset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Customer search</Label>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="e.g. S. Pine"
                    className="pl-8 rounded-md"
                    data-testid="redemptions-filter-search"
                  />
                </div>
              </div>
            </div>

            {preset === "custom" && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-md mt-1" data-testid="redemptions-filter-from" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wider text-gray-500">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-md mt-1" data-testid="redemptions-filter-to" />
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Button type="button" onClick={load} disabled={loading} size="sm" className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="redemptions-apply-button">
                {loading ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Loading…</> : "Apply filters"}
              </Button>
              <Button type="button" onClick={download} disabled={downloading || items.length === 0} size="sm" variant="outline" className="rounded-[35px]" data-testid="redemptions-download-button">
                {downloading ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Exporting…</> : <><Download size={13} className="mr-1.5" /> Export CSV</>}
              </Button>
            </div>
          </div>

          <div className="mt-4 border border-border rounded-md overflow-hidden bg-white" data-testid="redemptions-table">
            {loading ? (
              <div className="p-6 text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-sm text-gray-500" data-testid="redemptions-empty">
                No redemptions match these filters yet. Once you mark promo coupons or loyalty rewards as redeemed, they&apos;ll appear here.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-border">
                  <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Title / reward</th>
                    <th className="px-3 py-2">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50" data-testid={`redemptions-row-${r.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{humanDate(r.at)}</td>
                      <td className="px-3 py-2">
                        {r.kind === "loyalty" ? (
                          <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-mono">
                            <Gift size={10} className="mr-1" /> Loyalty
                          </Badge>
                        ) : (
                          <Badge className="rounded-sm bg-[#B31E1E]/10 text-[#B31E1E] border border-[#B31E1E]/30 text-[10px] font-mono">
                            <Tag size={10} className="mr-1" /> Promo
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-900">{r.customer_name_masked || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-gray-700 max-w-[280px] truncate" title={r.title || ""}>{r.title || "—"}</td>
                      <td className="px-3 py-2 text-gray-500 max-w-[280px] truncate" title={r.note || ""}>{r.note || <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
